/**
 * Anonymous usage counts (docs/plans/015-admin-analytics.md). Only event names and how often they happened are sent: no account,
 * no pet name, no identifier, no cookie. The names mirror the server's allow-list (`apps/server/src/events.ts`); anything else is dropped.
 */
export const EVENT_NAMES = [
  "app_open",
  "hatch",
  "care_food",
  "care_rest",
  "care_fun",
  "care_clean",
  "care_love",
  "badge_earned",
  "shop_open",
  "checkout_start",
  "promo_redeem",
  "signup_start",
  "signup_done",
  "signin_google_start",
  "book_open",
  "visit_start",
  "trophies_open",
  "more_open",
  "items_open",
  "settings_open",
  "friends_open",
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

export interface AnalyticsEvent {
  name: string;
  n: number;
}

/** Whether counts are sent until the player says otherwise (a legal question: see docs/SPEC.md section 12). */
export const ANALYTICS_DEFAULT_ON = true;

const KNOWN = new Set<string>(EVENT_NAMES);
/** The server accepts at most 100 per name per request. */
const MAX_PER_NAME = 100;

export interface AnalyticsDeps {
  enabled(): boolean;
  send(events: AnalyticsEvent[]): Promise<void>;
}

export function createAnalytics(deps: AnalyticsDeps) {
  const counts = new Map<string, number>();
  return {
    track(name: string) {
      if (!KNOWN.has(name) || !deps.enabled()) return;
      counts.set(name, Math.min(MAX_PER_NAME, (counts.get(name) ?? 0) + 1));
    },
    /** Sends what has been counted. A failed send keeps the counts for the next try. */
    async flush(): Promise<void> {
      if (!deps.enabled()) {
        counts.clear();
        return;
      }
      if (counts.size === 0) return;
      const batch = [...counts].map(([name, n]) => ({ name, n }));
      counts.clear();
      try {
        await deps.send(batch);
      } catch {
        for (const { name, n } of batch) counts.set(name, Math.min(MAX_PER_NAME, (counts.get(name) ?? 0) + n));
      }
    },
  };
}
